import {useEffect, useMemo, useState} from 'react';
import type {
  SmtOrderActionViewModel,
  SmtOrderAttentionCode,
  SmtOrdersFilter,
  SmtOrdersListItemViewModel,
} from '../../../../packages/runtime/smt-orders-contract.ts';
import type {FulfillmentState} from '../../../../packages/fulfillment/fulfillment-authority.ts';
import type {
  SmtOrdersUiIntent,
  SmtOrdersUiReadRequest,
  SmtOrdersViewModel,
  SmtRuntimePort,
} from '../contracts.ts';

type OrderStage = NonNullable<SmtOrdersFilter['status']>[number];
type OrderSource = NonNullable<SmtOrdersFilter['source']>[number];
type OrderChannel = NonNullable<SmtOrdersFilter['channel']>[number];
type PaymentEvidenceAction = Extract<SmtOrdersUiIntent, {actionId: 'payment.refund' | 'payment.correction'}>['actionId'];
type OperatorLane = 'onsite' | 'morefun-app' | 'delivery';

const stageLabels: Record<OrderStage, string> = {
  open: '處理中',
  ready: '可取',
  completed: '已完成',
  cancelled: '已取消',
};

const fulfillmentStateLabels: Record<FulfillmentState, string> = {
  held: '待製作',
  production_authorized: '製作中',
  ready: '可取',
  completed: '已完成',
  cancelled: '已取消',
};

const paymentLabels = {
  unsettled: '未收款',
  partial: '部分收款',
  settled: '已收款',
  credit: '有餘額',
} as const;

const attentionLabels: Record<SmtOrderAttentionCode, string> = {
  'payment.outstanding': '尚欠款',
  'payment.credit': '付款餘額',
  'fulfillment.cancelled': '已取消',
  'print.failed': '打印失敗',
  'print.unknown': '打印狀態未知',
};

const sourceLabels: Readonly<Record<OrderSource, string>> = {
  smt: '收銀台',
  smm: '手機收銀',
  customer: '磨飯 App',
  phone: '電話',
  whatsapp: 'WhatsApp',
  platform: '外賣平台',
};

const channelLabels: Readonly<Record<OrderChannel, string>> = {
  store: '現場',
  'customer-web': '磨飯 App',
  'whatsapp-phone': '電話／WhatsApp',
  'delivery-platform': '外賣平台',
};

const sourceFilterOrder = ['smt', 'smm', 'customer', 'phone', 'whatsapp', 'platform'] as const satisfies readonly OrderSource[];
const channelFilterOrder = ['store', 'customer-web', 'whatsapp-phone', 'delivery-platform'] as const satisfies readonly OrderChannel[];

const serviceLabels: Readonly<Record<string, string>> = {
  'dine-in': '堂食',
  takeaway: '外賣',
};

const printLabels: Readonly<Record<string, string>> = {
  not_requested: '未要求打印',
  queued: '待打印',
  dispatching: '打印中',
  accepted: '已接受',
  failed: '打印失敗',
  unknown: '狀態未知',
};

const laneMeta: Readonly<Record<OperatorLane,{label:string;hint:string}>> = {
  onsite: {label:'現場',hint:'收銀台／手機收銀／電話／WhatsApp'},
  'morefun-app': {label:'磨飯 App',hint:'自家 App／網站落單'},
  delivery: {label:'外賣平台',hint:'Foodpanda／Keeta 等平台'},
};

const laneOrder: readonly OperatorLane[] = ['onsite','morefun-app','delivery'];

const money = (minor: number, currency: string): string => new Intl.NumberFormat('zh-HK', {
  style: 'currency',
  currency,
  currencyDisplay: 'narrowSymbol',
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
}).format(minor / 100);

const time = (value: string | null): string => value
  ? new Intl.DateTimeFormat('zh-HK', {month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false}).format(new Date(value))
  : '—';

function orderTone(item: SmtOrdersListItemViewModel): string {
  if (item.attention.length) return 'attention';
  if (item.fulfillment.smt.stage === 'ready') return 'ready';
  if (item.fulfillment.smt.stage === 'completed') return 'completed';
  if (item.fulfillment.smt.stage === 'cancelled') return 'cancelled';
  return 'open';
}

function operatorLane(item:SmtOrdersListItemViewModel):OperatorLane {
  if(item.channel==='delivery-platform'||item.source==='platform') return 'delivery';
  if(item.channel==='customer-web'||item.source==='customer') return 'morefun-app';
  return 'onsite';
}

interface EvidenceForm {
  readonly actionId: PaymentEvidenceAction;
  readonly method: string;
  readonly amount: string;
  readonly reason: string;
  readonly direction: 'in' | 'out';
  readonly payerId: string;
  readonly reference: string;
}

const emptyEvidence = (actionId: PaymentEvidenceAction): EvidenceForm => ({
  actionId,
  method: '',
  amount: '',
  reason: '',
  direction: 'out',
  payerId: '',
  reference: '',
});

export function OrdersPage({runtime}: {runtime: SmtRuntimePort}) {
  const [identityQuery, setIdentityQuery] = useState('');
  const [stage, setStage] = useState<'all' | OrderStage>('all');
  const [source, setSource] = useState<'all' | OrderSource>('all');
  const [channel, setChannel] = useState<'all' | OrderChannel>('all');
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [view, setView] = useState<SmtOrdersViewModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mutationMessage, setMutationMessage] = useState<string | null>(null);
  const [evidence, setEvidence] = useState<EvidenceForm | null>(null);

  const request = useMemo<SmtOrdersUiReadRequest>(() => {
    const filter: SmtOrdersFilter = {
      ...(identityQuery.trim() ? {identityQuery: identityQuery.trim()} : {}),
      ...(stage === 'all' ? {} : {status: [stage]}),
      ...(source === 'all' ? {} : {source: [source]}),
      ...(channel === 'all' ? {} : {channel: [channel]}),
    };
    return {
      ...(Object.keys(filter).length ? {filter} : {}),
      ...(selectedOrderId ? {selectedOrderId} : {}),
    };
  }, [channel, identityQuery, selectedOrderId, source, stage]);

  useEffect(() => {
    let ignore = false;
    setLoading(true);
    runtime.readOrders(request)
      .then((next) => {
        if (ignore) return;
        setView(next);
        if(!selectedOrderId){
          const initial=next.selectedOrderId??next.items[0]?.orderId??null;
          if(initial)setSelectedOrderId(initial);
        }
        setError(null);
      })
      .catch((reason: unknown) => {
        if (ignore) return;
        setError(reason instanceof Error ? reason.message : 'SMT_ORDERS_READ_FAILED');
      })
      .finally(() => {
        if (!ignore) setLoading(false);
      });
    return () => { ignore = true; };
  }, [request, runtime, selectedOrderId]);

  const clearFilters = () => {
    setIdentityQuery('');
    setStage('all');
    setSource('all');
    setChannel('all');
  };

  const runIntent = async (intent: SmtOrdersUiIntent) => {
    setLoading(true);
    setMutationMessage(null);
    try {
      const result = await runtime.dispatchOrders(intent, request);
      setView(result.snapshot);
      setSelectedOrderId(result.snapshot.selectedOrderId);
      if (result.kind === 'rejected') {
        setMutationMessage(result.code);
      } else {
        setMutationMessage('操作已由系統核實');
        setEvidence(null);
      }
      setError(null);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'SMT_ORDERS_MUTATION_FAILED');
    } finally {
      setLoading(false);
    }
  };

  const onAction = (action: SmtOrderActionViewModel) => {
    const detail = view?.selectedOrder;
    if (!detail || !action.enabled) return;
    if (action.actionId === 'payment.refund' || action.actionId === 'payment.correction') {
      setEvidence(emptyEvidence(action.actionId));
      return;
    }
    if (action.requiredFulfillmentVersion === undefined) {
      setMutationMessage('SMT_ORDERS_FULFILLMENT_VERSION_REQUIRED');
      return;
    }
    const shared = {
      orderId: detail.order.orderId,
      expectedOrderRevision: action.requiredOrderRevision,
      expectedFulfillmentVersion: action.requiredFulfillmentVersion,
    };
    if (action.actionId === 'fulfillment.authorize-production') {
      void runIntent({actionId: 'fulfillment.authorize-production', ...shared});
    } else if (action.actionId === 'fulfillment.mark-ready') {
      void runIntent({actionId: 'fulfillment.mark-ready', ...shared});
    } else if (action.actionId === 'fulfillment.mark-completed') {
      void runIntent({actionId: 'fulfillment.mark-completed', ...shared});
    }
  };

  const submitEvidence = () => {
    const detail = view?.selectedOrder;
    const action = detail?.actions.find((candidate) => candidate.actionId === evidence?.actionId);
    if (!detail || !action || !evidence || !action.enabled || action.requiredPaymentVersion === undefined) return;
    const major = Number(evidence.amount);
    const amountMinor = Math.round(major * 100);
    if (!evidence.method.trim() || !evidence.reason.trim() || !Number.isFinite(amountMinor) || amountMinor <= 0) {
      setMutationMessage('請填寫付款方式、有效金額及原因');
      return;
    }
    const shared = {
      orderId: detail.order.orderId,
      expectedOrderRevision: action.requiredOrderRevision,
      expectedPaymentVersion: action.requiredPaymentVersion,
      businessDate: detail.order.businessDate,
      method: evidence.method.trim(),
      amountMinor,
      reason: evidence.reason.trim(),
      ...(evidence.payerId.trim() ? {payerId: evidence.payerId.trim()} : {}),
      ...(evidence.reference.trim() ? {reference: evidence.reference.trim()} : {}),
    };
    const intent: SmtOrdersUiIntent = evidence.actionId === 'payment.refund'
      ? {actionId: 'payment.refund', ...shared}
      : {actionId: 'payment.correction', direction: evidence.direction, ...shared};
    void runIntent(intent);
  };

  const selected = view?.selectedOrder;
  const items=view?.items??[];
  const laneItems=laneOrder.map(lane=>({lane,items:items.filter(item=>operatorLane(item)===lane)}));
  const hasActiveFilters=Boolean(identityQuery.trim()||stage!=='all'||source!=='all'||channel!=='all');

  return <main className="orders-page orders-operator-layout" aria-busy={loading}>
    <header className="orders-operator-toolbar glass-panel">
      <div className="orders-operator-title"><p className="eyebrow">Orders</p><h2>訂單</h2><span>左邊詳細資料 · 右邊按營運渠道分流</span></div>
      <label className="orders-search"><span>搜尋訂單</span><input value={identityQuery} onChange={(event)=>setIdentityQuery(event.target.value)} placeholder="訂單號" /></label>
      <div className="orders-stage-filter" role="group" aria-label="訂單狀態">
        <button aria-pressed={stage==='all'} className={stage==='all'?'active':''} onClick={()=>setStage('all')}>全部</button>
        {(Object.keys(stageLabels) as OrderStage[]).map(value=><button key={value} aria-pressed={stage===value} className={stage===value?'active':''} onClick={()=>setStage(value)}>{stageLabels[value]}</button>)}
      </div>
      <div className="orders-operator-toolbar-end">{hasActiveFilters?<button className="orders-filter-reset" onClick={clearFilters}>清除篩選</button>:null}<strong>{items.length} 單</strong></div>
    </header>

    <section className="orders-operator-filter-bar" aria-label="訂單篩選">
      <div className="orders-source-filter" role="group" aria-label="訂單來源">
        <button aria-pressed={source==='all'} className={source==='all'?'active':''} onClick={()=>setSource('all')}>全部來源</button>
        {sourceFilterOrder.map(value=><button key={value} aria-pressed={source===value} className={source===value?'active':''} onClick={()=>setSource(value)}>{sourceLabels[value]}</button>)}
      </div>
      <div className="orders-channel-filter" role="group" aria-label="訂單渠道">
        <button aria-pressed={channel==='all'} className={channel==='all'?'active':''} onClick={()=>setChannel('all')}>全部渠道</button>
        {channelFilterOrder.map(value=><button key={value} aria-pressed={channel===value} className={channel===value?'active':''} onClick={()=>setChannel(value)}>{channelLabels[value]}</button>)}
      </div>
    </section>

    {error?<div className="orders-error" role="alert">{error}</div>:null}

    <div className="orders-operator-body">
      <section className="orders-detail-column orders-detail glass-panel" aria-label="訂單詳細資料">
        {!selected?<div className="orders-detail-empty"><b>{loading?'讀取訂單中…':'選擇一張訂單'}</b><span>從右邊「現場／磨飯 App／外賣平台」選擇訂單。</span></div>:<>
          <header className="orders-detail-header">
            <div><p className="eyebrow">{sourceLabels[selected.order.source]} · {channelLabels[selected.order.channel]} · {selected.order.serviceMode?serviceLabels[selected.order.serviceMode]??selected.order.serviceMode:'—'}</p><h2>{selected.order.orderId}</h2><span>{selected.order.businessDate} · {time(selected.order.createdAt)}</span></div>
            <div className="orders-detail-total"><span>訂單總額</span><strong>{money(selected.order.totalMinor,selected.order.currency)}</strong></div>
          </header>
          {selected.attention.length?<div className="orders-detail-alert" role="status">{selected.attention.map(code=><span key={code}>{attentionLabels[code]}</span>)}</div>:null}
          <div className="orders-detail-grid">
            <section><h3>商品</h3><div className="orders-lines">{selected.order.lines.map(line=><article key={line.lineId}><div><strong>{line.productSnapshot.operationsName}</strong><b>×{line.quantity}</b></div>{line.productSnapshot.selectedOptions.length?<p>{line.productSnapshot.selectedOptions.map(option=>option.name).join(' · ')}</p>:null}{line.note?<small>備註：{line.note}</small>:null}</article>)}</div></section>
            <aside className="orders-facts"><h3>營運狀態</h3><dl><div><dt>製作</dt><dd>{fulfillmentStateLabels[selected.fulfillment.state]}</dd></div><div><dt>已收</dt><dd>{money(selected.payment.summary.netReceivedMinor,selected.payment.currency)}</dd></div><div><dt>尚欠</dt><dd>{money(selected.payment.summary.outstandingMinor,selected.payment.currency)}</dd></div><div><dt>可退款</dt><dd>{money(selected.payment.summary.refundableMinor,selected.payment.currency)}</dd></div><div><dt>打印</dt><dd>{printLabels[selected.logicalPrint.state]??selected.logicalPrint.state}</dd></div><div><dt>版本</dt><dd>O r{selected.provenance.orderRevision} · P v{selected.provenance.paymentVersion} · F v{selected.provenance.fulfillmentVersion}</dd></div></dl></aside>
          </div>
          <section className="orders-actions"><div className="orders-actions-title"><h3>可用操作</h3>{loading?<span>系統核實中…</span>:null}</div><div className="orders-action-buttons">{selected.actions.map(action=><button key={action.actionId} disabled={!action.enabled||loading} title={action.disabledReason??action.label} className={action.actionId.startsWith('payment.')?'payment-action':'fulfillment-action'} onClick={()=>onAction(action)}><b>{action.label}</b>{!action.enabled&&action.disabledReason?<small>{action.disabledReason}</small>:null}</button>)}</div>{mutationMessage?<div className="orders-mutation-message" role="status">{mutationMessage}</div>:null}</section>
          {evidence?<section className="orders-evidence" aria-label={evidence.actionId==='payment.refund'?'退款資料':'付款更正資料'}><div className="orders-evidence-heading"><div><p className="eyebrow">需要操作證據</p><h3>{evidence.actionId==='payment.refund'?'退款':'付款更正'}</h3></div><button aria-label="關閉付款操作" onClick={()=>setEvidence(null)}>×</button></div><div className="orders-evidence-fields"><label><span>付款方式</span><input value={evidence.method} onChange={event=>setEvidence({...evidence,method:event.target.value})} placeholder="例如 CASH / FPS" /></label><label><span>金額</span><input inputMode="decimal" value={evidence.amount} onChange={event=>setEvidence({...evidence,amount:event.target.value})} placeholder="HKD" /></label>{evidence.actionId==='payment.correction'?<label><span>方向</span><select value={evidence.direction} onChange={event=>setEvidence({...evidence,direction:event.target.value as 'in'|'out'})}><option value="in">收入更正</option><option value="out">支出更正</option></select></label>:null}<label className="wide"><span>原因</span><input value={evidence.reason} onChange={event=>setEvidence({...evidence,reason:event.target.value})}/></label><label><span>付款人 ID（可選）</span><input value={evidence.payerId} onChange={event=>setEvidence({...evidence,payerId:event.target.value})}/></label><label><span>參考編號（可選）</span><input value={evidence.reference} onChange={event=>setEvidence({...evidence,reference:event.target.value})}/></label></div><button className="orders-evidence-submit" onClick={submitEvidence}>提交系統核實</button></section>:null}
        </>}
      </section>

      <section className="orders-channel-board" aria-label="訂單渠道">
        {laneItems.map(({lane,items:laneOrders})=>{
          const totalMinor=laneOrders.reduce((sum,item)=>sum+item.totalMinor,0);
          const currency=laneOrders[0]?.currency??'HKD';
          return <section className="orders-channel-lane glass-panel" data-channel={lane} key={lane}>
            <header><div><h3>{laneMeta[lane].label}</h3><span>{laneMeta[lane].hint}</span></div><div><b>{laneOrders.length}</b><strong>{money(totalMinor,currency)}</strong></div></header>
            <div className="orders-lane-list">{laneOrders.length?laneOrders.map(item=><button key={item.orderId} aria-pressed={selectedOrderId===item.orderId} className={`orders-row ${orderTone(item)} ${selectedOrderId===item.orderId?'selected':''}`} onClick={()=>setSelectedOrderId(item.orderId)}><div className="orders-row-top"><strong>{item.orderIdLabel}</strong><span>{time(item.createdAt)}</span></div><div className="orders-row-meta"><span>{sourceLabels[item.source]}</span><span>{item.serviceMode?serviceLabels[item.serviceMode]??item.serviceMode:'—'}</span><span>{item.itemCount} 件</span></div><div className="orders-row-status"><b>{stageLabels[item.fulfillment.smt.stage]}</b><span>{paymentLabels[item.payment.state]}</span><strong>{money(item.totalMinor,item.currency)}</strong></div>{item.attention.length?<div className="orders-attention">{item.attention.map(code=>attentionLabels[code]).join(' · ')}</div>:null}</button>):<div className="orders-lane-empty">沒有符合條件的訂單</div>}</div>
          </section>;
        })}
      </section>
    </div>
  </main>;
}